import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';
import { TimeLog } from '../../../types/timeReport';
import { parseUTCTimestamp } from '../../../utils/dateUtils';
import { TEXAS_TIMEZONE } from '../../../constants/timezone';
import { setShiftTo8Hours, updateTimeLog } from '../../../lib/services/timeReportService';

interface EditShiftModalProps {
  shift: TimeLog | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  adminUserId: string;
}

const EditShiftModal: React.FC<EditShiftModalProps> = ({
  shift,
  isOpen,
  onClose,
  onSave,
  adminUserId,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setError(null);
    }
  }, [isOpen]);

  if (!isOpen || !shift) return null;

  const clockInTime = parseUTCTimestamp(shift.clock_in_time);
  const clockOutTime = shift.clock_out_time ? parseUTCTimestamp(shift.clock_out_time) : null;

  const formatDateTimeLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const handleSet8Hours = async () => {
    try {
      setIsLoading(true);
      setError(null);

      await setShiftTo8Hours(shift.id, shift.clock_in_time, adminUserId);

      onSave();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to update shift');
    } finally {
      setIsLoading(false);
    }
  };

  const handleManualSave = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const clockInEl = document.getElementById('edit-clock-in') as HTMLInputElement;
      const clockOutEl = document.getElementById('edit-clock-out') as HTMLInputElement;
      const notesEl = document.getElementById('admin-notes') as HTMLTextAreaElement;

      // Validation
      if (!clockOutEl.value) {
        setError('Please enter a clock out time');
        setIsLoading(false);
        return;
      }

      if (!notesEl.value.trim()) {
        setError('Please enter admin notes explaining the correction');
        setIsLoading(false);
        return;
      }

      // Convert to UTC from Texas timezone
      const clockInLocal = new Date(clockInEl.value);
      const clockOutLocal = new Date(clockOutEl.value);

      const clockInUTC = fromZonedTime(clockInLocal, TEXAS_TIMEZONE);
      const clockOutUTC = fromZonedTime(clockOutLocal, TEXAS_TIMEZONE);

      // Validate: clock out must be after clock in
      if (clockOutUTC <= clockInUTC) {
        setError('Clock out time must be after clock in time');
        setIsLoading(false);
        return;
      }

      await updateTimeLog(shift.id, {
        clock_in_time: clockInUTC.toISOString(),
        clock_out_time: clockOutUTC.toISOString(),
        status: 'clocked_out',
        admin_notes: notesEl.value.trim(),
        edited_by: adminUserId,
      });

      onSave();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to update shift');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b flex justify-between items-center">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Edit Time Entry</h3>
            <p className="text-sm text-gray-500 mt-1">
              {shift.staff_name} - {format(clockInTime, 'MMM d, yyyy')}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="text-gray-400 hover:text-gray-500"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Current Data Display */}
          <div className="bg-gray-50 p-4 rounded space-y-2">
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-700">Clock In:</span>
              <span className="text-sm text-gray-900">
                {format(clockInTime, 'h:mm a')}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-700">Clock Out:</span>
              {clockOutTime ? (
                <span className="text-sm text-gray-900">
                  {format(clockOutTime, 'h:mm a')}
                </span>
              ) : (
                <span className="text-sm text-red-600 font-medium">Missing ⚠️</span>
              )}
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-700">Status:</span>
              <span
                className={`text-sm font-medium ${
                  shift.status === 'clocked_out' ? 'text-green-600' : 'text-yellow-600'
                }`}
              >
                {shift.status === 'clocked_out' ? 'Complete' : 'Incomplete'}
              </span>
            </div>
            {shift.admin_edited && (
              <div className="pt-2 border-t border-gray-200">
                <span className="text-xs text-blue-600">
                  Previously edited by {shift.edited_by_name || 'admin'}
                </span>
                {shift.admin_notes && (
                  <p className="text-xs text-gray-600 mt-1">{shift.admin_notes}</p>
                )}
              </div>
            )}
          </div>

          {/* Option 1: Set to 1 Hour (Quick Fix for forgotten clock-outs) */}
          <div>
            <button
              onClick={handleSet8Hours}
              disabled={isLoading}
              className="w-full py-3 px-4 border-2 border-blue-500 text-blue-600 rounded-md hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ✅ Set to 1 Hour (Default)
            </button>
            <p className="text-xs text-gray-500 mt-1 text-center">
              This will set clock out to 1 hour after clock in (for forgotten shifts)
            </p>
          </div>

          {/* Option 2: Manual Time Entry */}
          <div className="border-t pt-4">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Or enter exact times:
            </label>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="edit-clock-in" className="block text-xs text-gray-500 mb-1">
                  Clock In
                </label>
                <input
                  type="datetime-local"
                  id="edit-clock-in"
                  defaultValue={formatDateTimeLocal(clockInTime)}
                  disabled={isLoading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label htmlFor="edit-clock-out" className="block text-xs text-gray-500 mb-1">
                  Clock Out
                </label>
                <input
                  type="datetime-local"
                  id="edit-clock-out"
                  defaultValue={clockOutTime ? formatDateTimeLocal(clockOutTime) : ''}
                  disabled={isLoading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>

            {/* Admin Notes */}
            <div className="mt-4">
              <label htmlFor="admin-notes" className="block text-sm font-medium text-gray-700 mb-2">
                Admin Notes <span className="text-red-500">*</span>
              </label>
              <textarea
                id="admin-notes"
                rows={3}
                disabled={isLoading}
                placeholder="Reason for correction (e.g., 'Staff forgot to clock out')"
                defaultValue={shift.admin_notes || ''}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              ></textarea>
            </div>
          </div>

          {/* Location Data Display */}
          {shift.clock_in_latitude && (
            <div className="text-xs text-gray-500 border-t pt-3 space-y-1">
              <p className="flex items-center">
                <span className="mr-2">📍</span>
                <span>
                  Clock In Location:{' '}
                  <a
                    href={`https://www.google.com/maps?q=${shift.clock_in_latitude},${shift.clock_in_longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    {shift.clock_in_latitude.toFixed(6)}, {shift.clock_in_longitude?.toFixed(6)}
                  </a>
                </span>
              </p>
              {shift.clock_out_latitude && (
                <p className="flex items-center">
                  <span className="mr-2">📍</span>
                  <span>
                    Clock Out Location:{' '}
                    <a
                      href={`https://www.google.com/maps?q=${shift.clock_out_latitude},${shift.clock_out_longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      {shift.clock_out_latitude.toFixed(6)}, {shift.clock_out_longitude?.toFixed(6)}
                    </a>
                  </span>
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end space-x-3">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleManualSave}
            disabled={isLoading}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditShiftModal;
